import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Bed, Bath, Maximize2, Building2, Eye, Phone, Mail, ArrowLeft, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

export default function ListingPage() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/listings/public/${slug}`);
        setListing(res.data);
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
      }
      setLoading(false);
    };
    fetch();
  }, [slug]);

  useSEO({
    title: listing ? `${listing.title} | Krieger Real Estate` : 'Property Listing | Krieger Real Estate',
    description: listing ? `${listing.title} — ${listing.address}, ${listing.city}, ${listing.state}. ${fmt(listing.price)}. Listed by ${listing.agent_name} at Krieger Real Estate.` : '',
    canonical: `https://joinkrieger.com/listing/${slug}`,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-28">
        <div className="max-w-4xl mx-auto px-4 animate-pulse space-y-6">
          <div className="h-80 bg-slate-200 rounded-xl" />
          <div className="h-8 bg-slate-200 rounded w-2/3" />
          <div className="h-4 bg-slate-200 rounded w-1/3" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 pt-28">
        <div className="max-w-3xl mx-auto px-4 text-center py-20">
          <Building2 size={48} className="text-slate-200 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Listing Not Found</h1>
          <p className="text-slate-500 mb-8">This listing may have been sold, taken off market, or the link is incorrect.</p>
          <Link to="/"><Button variant="outline" className="rounded-full">Back to Home</Button></Link>
        </div>
      </div>
    );
  }

  const images = listing.images || [];
  const share = () => {
    if (navigator.share) {
      navigator.share({ title: listing.title, text: `${listing.title} — ${fmt(listing.price)}`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#0F1729] text-white pt-24 pb-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 mb-4 transition-colors">
            <ArrowLeft size={16} /> Krieger Real Estate
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-2">
        {/* Photo Gallery */}
        {images.length > 0 ? (
          <div className="relative rounded-xl overflow-hidden mb-6 bg-slate-900" data-testid="listing-gallery">
            <img
              src={`${API}${images[activePhoto]?.url}`}
              alt={listing.title}
              className="w-full h-[400px] object-cover"
            />
            {images.length > 1 && (
              <>
                <button onClick={() => setActivePhoto(p => (p - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"><ChevronLeft size={20} /></button>
                <button onClick={() => setActivePhoto(p => (p + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"><ChevronRight size={20} /></button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setActivePhoto(i)} className={`w-2 h-2 rounded-full transition-all ${i === activePhoto ? 'bg-white w-4' : 'bg-white/40'}`} />
                  ))}
                </div>
              </>
            )}
            {/* Thumbnail strip */}
            {images.length > 1 && (
              <div className="flex gap-1.5 mt-2 px-1">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActivePhoto(i)} className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${i === activePhoto ? 'border-[#002FA7]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                    <img src={`${API}${img.url}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="h-64 bg-slate-100 rounded-xl mb-6 flex items-center justify-center">
            <Building2 size={48} className="text-slate-200" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-16">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20 text-[10px] uppercase tracking-wider capitalize">{listing.property_type}</Badge>
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wider capitalize">{listing.status?.replace('_', ' ')}</Badge>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold font-['Cabinet_Grotesk'] text-slate-900" data-testid="listing-title">
                  {listing.title}
                </h1>
                <p className="flex items-center gap-1.5 text-sm text-slate-500 mt-1">
                  <MapPin size={14} /> {listing.address}, {listing.city}, {listing.state}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={share} className="rounded-full gap-1 shrink-0" data-testid="share-btn">
                <Share2 size={14} /> Share
              </Button>
            </div>

            <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7] mb-6" data-testid="listing-price">
              {fmt(listing.price)}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-4 mb-8 p-4 bg-white rounded-xl border border-slate-200">
              {listing.bedrooms && (
                <div className="flex items-center gap-2"><Bed size={18} className="text-slate-400" /><div><div className="text-lg font-bold text-slate-800">{listing.bedrooms}</div><div className="text-[10px] text-slate-400 uppercase">Beds</div></div></div>
              )}
              {listing.bathrooms && (
                <div className="flex items-center gap-2"><Bath size={18} className="text-slate-400" /><div><div className="text-lg font-bold text-slate-800">{listing.bathrooms}</div><div className="text-[10px] text-slate-400 uppercase">Baths</div></div></div>
              )}
              {listing.sqft && (
                <div className="flex items-center gap-2"><Maximize2 size={18} className="text-slate-400" /><div><div className="text-lg font-bold text-slate-800">{listing.sqft.toLocaleString()}</div><div className="text-[10px] text-slate-400 uppercase">Sq Ft</div></div></div>
              )}
              {listing.year_built && (
                <div className="flex items-center gap-2"><Building2 size={18} className="text-slate-400" /><div><div className="text-lg font-bold text-slate-800">{listing.year_built}</div><div className="text-[10px] text-slate-400 uppercase">Built</div></div></div>
              )}
              <div className="flex items-center gap-2"><Eye size={18} className="text-slate-400" /><div><div className="text-lg font-bold text-slate-800">{listing.views || 0}</div><div className="text-[10px] text-slate-400 uppercase">Views</div></div></div>
            </div>

            {/* Description */}
            {listing.description && (
              <div className="mb-8">
                <h2 className="text-lg font-['Cabinet_Grotesk'] font-bold text-slate-900 mb-3">Description</h2>
                <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{listing.description}</p>
              </div>
            )}

            {/* Features */}
            {listing.features && listing.features.length > 0 && (
              <div>
                <h2 className="text-lg font-['Cabinet_Grotesk'] font-bold text-slate-900 mb-3">Features</h2>
                <div className="grid grid-cols-2 gap-2">
                  {listing.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-600"><span className="w-1.5 h-1.5 rounded-full bg-[#002FA7] shrink-0" />{f}</div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar — Agent Card */}
          <div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 sticky top-24" data-testid="agent-card">
              <div className="text-center mb-4">
                <div className="w-16 h-16 rounded-full bg-[#EFF3FF] flex items-center justify-center text-xl font-bold text-[#002FA7] mx-auto mb-3">
                  {(listing.agent_name || 'A').charAt(0)}
                </div>
                <h3 className="text-base font-semibold text-slate-900">{listing.agent_name || 'Krieger Agent'}</h3>
                <p className="text-xs text-slate-400">Krieger Real Estate</p>
              </div>
              <div className="space-y-2 mb-4">
                {listing.agent_email && (
                  <a href={`mailto:${listing.agent_email}?subject=Inquiry about ${listing.title}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#002FA7] transition-colors p-2 rounded-lg hover:bg-slate-50">
                    <Mail size={14} className="text-slate-400" /> {listing.agent_email}
                  </a>
                )}
                {listing.agent_phone && (
                  <a href={`tel:${listing.agent_phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#002FA7] transition-colors p-2 rounded-lg hover:bg-slate-50">
                    <Phone size={14} className="text-slate-400" /> {listing.agent_phone}
                  </a>
                )}
              </div>
              {listing.agent_email && (
                <a href={`mailto:${listing.agent_email}?subject=Inquiry about ${listing.title}&body=Hi, I'm interested in the property at ${listing.address}. Please send me more information.`}>
                  <Button className="w-full bg-[#002FA7] hover:bg-[#002075] text-white rounded-lg" data-testid="contact-agent-btn">
                    Contact Agent
                  </Button>
                </a>
              )}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-[10px] text-slate-400 text-center">Listed by Krieger Real Estate<br/>NY &bull; PA &bull; FL &bull; joinkrieger.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
